// LeadScoreBadge -- subtle chip styled to match the rest of the agent app
// (same light-fill / coloured-border / coloured-text pattern used by the
// AI Notes button and Notification chips). Hovering reveals a popover that
// summarises the per-signal score breakdown, with a `?` button that flips
// the popover to a "How is this calculated?" explainer (mirrors the help
// toggle on AIHandoverNotesPopover).
//
// Props:
//   score      number 0..100 (required to render anything)
//   components optional object from production.contact_lead_scores.components
//              { is_unsubbed, raw_sum, agency_pct,
//                events: { <event_name>: { weight, count, decayed_sum, last_ts } } }
//   lastEventAt optional epoch seconds of most recent contributing event
//   size       "sm" (default) | "lg"

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaFire, FaRegQuestionCircle, FaTimes } from "react-icons/fa";

function tierFor(n) {
  if (n >= 90) return { key: "blazing", label: "Blazing", bg: "#fee2e2", border: "#fca5a5", fg: "#7f1d1d", dot: "#dc2626" };
  if (n >= 70) return { key: "hot",     label: "Hot",     bg: "#fee2e2", border: "#fecaca", fg: "#991b1b", dot: "#ef4444" };
  if (n >= 55) return { key: "warm",    label: "Warm",    bg: "#ffedd5", border: "#fed7aa", fg: "#9a3412", dot: "#f97316" };
  if (n >= 40) return { key: "mild",    label: "Mild",    bg: "#fef3c7", border: "#fde68a", fg: "#92400e", dot: "#f59e0b" };
  if (n >= 20) return { key: "cool",    label: "Cool",    bg: "#dbeafe", border: "#bfdbfe", fg: "#1e3a8a", dot: "#3b82f6" };
  if (n >= 1)  return { key: "cold",    label: "Cold",    bg: "#f1f5f9", border: "#e2e8f0", fg: "#334155", dot: "#64748b" };
  return         { key: "dead",    label: "Dead",    bg: "#f3f4f6", border: "#e5e7eb", fg: "#6b7280", dot: "#9ca3af" };
}

const EVENT_LABELS = {
  property_analysed: "Property analysis",
  signed_up:         "Sign-up",
  sign_up:           "Sign-up",
  login:             "Login",
  property_search:   "Property search",
  suburb_searched:   "Suburb search",
  property_viewed:   "Property view",
  address_searched:  "Address search",
  email_open:        "Email open",
  email_click_au:    "Email click (AU)",
  sms_reply:         "SMS reply",
  handover_note:     "Agent note",
};

function labelFor(eventName) {
  if (EVENT_LABELS[eventName]) return EVENT_LABELS[eventName];
  // humanise unknown event_name -> "Some Event Name"
  return String(eventName || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtAgo(epochSec) {
  if (!epochSec) return null;
  const diff = Date.now() / 1000 - Number(epochSec);
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 60) return `${Math.round(diff / 86400)}d ago`;
  return `${Math.round(diff / (86400 * 30))}mo ago`;
}

export default function LeadScoreBadge({ score, components, lastEventAt, size = "sm", title }) {
  const hasScore = !(score === undefined || score === null);
  const n = hasScore ? Math.max(0, Math.min(100, Number(score) || 0)) : 0;
  const t = tierFor(n);

  const padY = size === "lg" ? 4 : 3;
  const padX = size === "lg" ? 10 : 8;
  const fontSize = size === "lg" ? 12 : 11;
  const numSize = size === "lg" ? 13 : 12;

  // ---------- popover state ----------
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const clearTimers = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };
  const scheduleOpen = () => {
    clearTimers();
    openTimer.current = setTimeout(() => setOpen(true), 120);
  };
  const scheduleClose = () => {
    if (pinned) return;
    clearTimers();
    closeTimer.current = setTimeout(() => { setOpen(false); setShowHelp(false); }, 180);
  };
  const closeNow = () => {
    clearTimers();
    setOpen(false);
    setPinned(false);
    setShowHelp(false);
  };

  useEffect(() => () => clearTimers(), []);

  // Position the popover next to the trigger, keeping it onscreen.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) { setPos(null); return; }
    const r = triggerRef.current.getBoundingClientRect();
    const W = 300;
    const H = 320;
    let top = r.bottom + 6;
    if (top + H > window.innerHeight - 8) top = Math.max(8, r.top - H - 6);
    let left = r.left;
    if (left + W > window.innerWidth - 8) left = Math.max(8, window.innerWidth - W - 8);
    setPos({ top, left });
  }, [open]);

  // Outside click to dismiss when pinned.
  useEffect(() => {
    if (!pinned) return;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      closeNow();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pinned]);

  if (!hasScore) return null;

  const isUnsubbed = !!components?.is_unsubbed;
  const rawSum = Number(components?.raw_sum || 0);
  const agencyPct = Number(components?.agency_pct || 0);
  const eventsObj = (components && components.events) || {};
  const eventRows = Object.entries(eventsObj)
    .map(([name, v]) => ({
      name,
      label: labelFor(name),
      weight: Number(v?.weight || 0),
      count: Number(v?.count || 0),
      decayedSum: Number(v?.decayed_sum || 0),
      lastTs: v?.last_ts ? Number(v.last_ts) : null,
    }))
    .sort((a, b) => b.decayedSum - a.decayedSum);
  const topDecayed = eventRows.length ? eventRows[0].decayedSum : 0;
  const lastAgo = fmtAgo(lastEventAt);

  const popover = open && pos ? ReactDOM.createPortal(
    <div
      ref={popRef}
      onMouseEnter={clearTimers}
      onMouseLeave={scheduleClose}
      style={{
        position: "fixed", top: pos.top, left: pos.left,
        width: 300, maxHeight: 360,
        background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
        boxShadow: "0 10px 30px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
        zIndex: 2000, display: "flex", flexDirection: "column",
        fontFamily: "inherit",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "10px 12px 8px", borderBottom: "1px solid #f3f4f6",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#111827" }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%", background: t.dot, flexShrink: 0,
            }} />
            Lead Score
            <button
              type="button"
              onClick={() => setShowHelp((v) => !v)}
              title={showHelp ? "Hide explanation" : "How is this calculated?"}
              aria-expanded={showHelp}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: showHelp ? "#0d9488" : "#9ca3af", padding: 2,
                fontSize: 12, lineHeight: 1, display: "inline-flex", alignItems: "center",
              }}
            >
              <FaRegQuestionCircle />
            </button>
          </div>
          <div style={{ marginTop: 2, fontSize: 12, color: "#6b7280" }}>
            <strong style={{ color: t.fg }}>{n}/100 · {t.label}</strong>
            {lastAgo && <> · last activity {lastAgo}</>}
          </div>
        </div>
        <button
          onClick={closeNow}
          title="Close"
          style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#9ca3af", padding: 4, fontSize: 12, lineHeight: 1,
          }}
        >
          <FaTimes />
        </button>
      </div>

      {/* Body */}
      <div style={{ overflowY: "auto", padding: "10px 12px", flex: 1 }}>
        {showHelp ? (
          <div style={{
            padding: "10px 12px", borderRadius: 8,
            background: "#f0fdfa", border: "1px solid #ccfbf1",
            fontSize: 11.5, color: "#0f766e", lineHeight: 1.5,
          }}>
            Each contributing event has a <strong>base weight</strong>. We then
            apply <strong>time decay</strong> (30-day half-life) so older
            actions count for less, sum everything per contact, and
            <strong> rank you within your agency on a curve</strong> &mdash; the
            top is exclusive (only standout contacts get 70+), and most
            of your book sits in the lower range. Contacts with no
            activity stay at 0.
            <div style={{ marginTop: 6, color: "#115e59" }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Base weights:</div>
              <div>• Property analysis — 25</div>
              <div>• SMS reply — 20</div>
              <div>• Email click (AU) — 10</div>
              <div>• Agent note — 8</div>
              <div>• Sign-up / Login — 6</div>
              <div>• Property / suburb / address search, property view — 5</div>
              <div>• Other platform activity — 3</div>
              <div>• Email open — 2</div>
            </div>
            <div style={{ marginTop: 6 }}>
              Decay: an event is worth <strong>half</strong> after 30 days,
              a quarter after 60 days, etc. Events older than 180 days are
              ignored.
            </div>
            <div style={{ marginTop: 6 }}>
              Anyone who unsubscribed (email or SMS STOP) is forced to <strong>0</strong>.
            </div>
          </div>
        ) : !components ? (
          <div style={{ padding: "12px 0", textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
            No breakdown available yet.
            <div style={{ marginTop: 4, fontSize: 11 }}>
              Scores refresh nightly at 4am.
            </div>
          </div>
        ) : isUnsubbed ? (
          <div style={{
            padding: "10px 12px", borderRadius: 8,
            background: "#fef2f2", border: "1px solid #fecaca",
            fontSize: 12, color: "#991b1b", lineHeight: 1.5,
          }}>
            <strong>Unsubscribed</strong> — this contact opted out of email or
            replied STOP to an SMS, so the score is forced to <strong>0</strong>.
          </div>
        ) : eventRows.length === 0 ? (
          <div style={{ padding: "12px 0", textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
            No contributing events in the last 180 days.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {eventRows.map((row) => {
                const denom = topDecayed > 0 ? topDecayed : 1;
                const pct = Math.max(0, Math.min(1, row.decayedSum / denom));
                const ago = fmtAgo(row.lastTs);
                const rawTotal = row.count * row.weight;
                return (
                  <div key={row.name} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <div style={{
                      display: "flex", justifyContent: "space-between", alignItems: "baseline",
                      gap: 8, fontSize: 11.5, color: "#374151",
                    }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                        <span style={{ fontWeight: 600 }}>{row.label}</span>
                        <span style={{ color: "#9ca3af", marginLeft: 6, fontSize: 10.5 }}>
                          {row.count} × {row.weight}pts = {rawTotal}
                        </span>
                      </span>
                      <span
                        style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, flexShrink: 0 }}
                        title={`After 30-day half-life decay: ${row.decayedSum.toFixed(2)} of ${rawTotal} raw points`}
                      >
                        {row.decayedSum.toFixed(1)}
                      </span>
                    </div>
                    <div style={{
                      height: 4, borderRadius: 999, background: "#f3f4f6", overflow: "hidden",
                    }}>
                      <div style={{
                        width: `${pct * 100}%`, height: "100%", background: t.dot,
                      }} />
                    </div>
                    {ago && (
                      <div style={{ fontSize: 10.5, color: "#9ca3af" }}>last {ago}</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={{
              marginTop: 10, paddingTop: 8, borderTop: "1px solid #f3f4f6",
              fontSize: 11.5, color: "#6b7280", display: "flex", flexDirection: "column", gap: 3,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span title="Sum of every event's decayed contribution">Total points (after decay)</span>
                <span style={{ fontVariantNumeric: "tabular-nums", color: "#374151", fontWeight: 600 }}>
                  {rawSum.toFixed(1)}
                </span>
              </div>
              {agencyPct > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Rank in your agency</span>
                  <span style={{ fontVariantNumeric: "tabular-nums", color: "#374151", fontWeight: 600 }}>
                    Top {Math.max(1, Math.round((1 - agencyPct) * 100))}%
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => { clearTimers(); scheduleOpen(); }}
        onMouseLeave={scheduleClose}
        onClick={(e) => {
          e.stopPropagation();
          clearTimers();
          if (open && pinned) { closeNow(); return; }
          setOpen(true);
          setPinned(true);
        }}
        title={title || `Lead Score ${n}/100 — ${t.label}`}
        aria-label={`Lead Score ${n} out of 100, ${t.label}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: `${padY}px ${padX}px`, borderRadius: 999,
          background: t.bg, border: `1px solid ${t.border}`, color: t.fg,
          fontSize, fontWeight: 600, lineHeight: 1.2, whiteSpace: "nowrap",
          flexShrink: 0, cursor: "pointer",
        }}
      >
        {n >= 70 && <FaFire style={{ fontSize: fontSize - 1 }} aria-hidden="true" />}
        <span style={{ opacity: 0.85, fontWeight: 600 }}>Score</span>
        <span style={{ fontSize: numSize, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
          {n}
        </span>
      </span>
      {popover}
    </>
  );
}
